import React from "react";
import { GridColDef } from "@mui/x-data-grid";
import { useTranslate } from "@refinedev/core";
import { TableCell } from "@shared/components";
import { DeleteButton, EditButton } from "@refinedev/mui";
import { Button, Link, Stack } from "@mui/material";
import { AgreementProduct } from "@modules/agreements/types/agreementProduct";
import { useQuery } from "@tanstack/react-query";
import { getProductByIdService } from "@modules/defaultProducts";

interface AgreementProductsColumnsProps {
  onRefetch: () => void;
  onViewPlans: (row: AgreementProduct) => void;
  onView: (row: AgreementProduct) => void;
  onEdit: (row: AgreementProduct) => void;
}

export const useAgreementProductsColumns = ({
  onRefetch,
  onViewPlans,
  onEdit,
}: AgreementProductsColumnsProps): GridColDef[] => {
  const translate = useTranslate();

  return React.useMemo<GridColDef[]>(
    () => [
      {
        field: "id",
        headerName: "ID",
        width: 80,
        sortable: true,
      },
      {
        field: "name",
        headerName: translate("agreement_product_admin.fields.name"),
        minWidth: 200,
        flex: 1,
      },
      {
        field: "metadata",
        headerName: 'Página',
        minWidth: 200,
        flex: 1,
        renderCell: ({ row }) => {
          const isBeta = import.meta.env.VITE_BACKEND_URL.includes('staging');
          let url = '';
          if (row.default_product?.is_bookstore && row.default_product?.create_landing && row.metadata?.slug) {
            url = `${isBeta ? `https://${row.metadata?.slug.value}.beta.ebooks.app.br` : `https://${row.metadata?.slug.value}.ebooks.app.br`}`;
          } else if (row.default_product?.is_superapp && row.default_product?.create_landing && row.metadata?.slug) {
            url = `${isBeta ? `https://${row.metadata?.slug.value}.beta.partiu.com.br` : `https://${row.metadata?.slug.value}.partiu.com.br`}`;
          }

          return (
            <TableCell.Container align="center">
              {url ? (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ textTransform: "none" }}
                >
                  Abrir Página
                </Button>
              ) : (
                '-'
              )}
            </TableCell.Container>
          );
        },
      },
      {
        field: "active",
        headerName: translate("agreement_product_admin.fields.active"),
        headerAlign: "center",
        align: "center",
        minWidth: 120,
        renderCell: ({ value }) => (
          <TableCell.Container align="center">
            {value ? translate("status.active") : translate("status.inactive")}
          </TableCell.Container>
        ),
      },
      {
        field: "default_product",
        headerName: translate("agreement_product_admin.fields.global_product"),
        minWidth: 160,
        renderCell: ({ value }) => {
          return (
            <TableCell.Fetch
              align="center"
              resourceHook={useListProducts}
              id={value?.id}
              valueKey="name"
            />
          );
        },
      },
      {
        field: "actions",
        headerName: translate("table.actions"),
        headerAlign: "center",
        minWidth: 260,
        sortable: false,
        renderCell: ({ row }) => (
          <TableCell.Container align="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant="text"
                onClick={() => onViewPlans(row)}
              >
                {translate("agreement_product_admin.actions.view_plans")}
              </Button>

              <EditButton hideText onClick={() => onEdit(row)} />

              <DeleteButton
                hideText
                recordItemId={row.id}
                resource="v1/product_admin"
                onSuccess={onRefetch}
              />
            </Stack>
          </TableCell.Container>
        ),
      },
    ],
    [onEdit, onRefetch, onViewPlans, translate]
  );
};

export const listProductsQueryKey = ["products"];

export function useListProducts(id: any, options?: any) {
  const formatedId = id?.ids?.[0];

  return useQuery({
    queryKey: listProductsQueryKey.concat(id),
    queryFn: async () => getProductByIdService(formatedId),
    ...options,
  });
}
